/**
 * Kintsugi Design System — raw color primitives (Styleguide).
 *
 * Authored in [Kintsugi Design System — Colors](https://www.figma.com/design/Wu3a1TfDV25CLd4tY8Sb0T/Kintsugi-Design--System?node-id=1133-25094&m=dev).
 *
 * Chakra mapping (legacy names kept for app compatibility):
 * - **Primary** → `colors.brand` / `colorPalette.brand`
 * - **Accent** → `colors.neonGreen` / `colorPalette.neonGreen`
 * - **Secondary** → `colors.purple`
 * - **Neutral** → `colors.neutral`
 */
export const colorPrimitives = {
  neutral: {
    0: '#FFFFFF',
    50: '#F6F6F6',
    100: '#E7E7E7',
    200: '#D1D1D1',
    250: '#888888',
    300: '#595959',
    350: '#454545',
    400: '#3D3D3D',
    500: '#000000',
  },
  primary: {
    50: '#F4F9F8',
    100: '#DBECE7',
    200: '#B7D8D1',
    300: '#8BBDB3',
    400: '#49837A',
    500: '#396862',
    600: '#2A4542',
    700: '#213331',
    800: '#122120',
    900: '#122120',
  },
  accent: {
    50: '#EEFCE9',
    100: '#DFF8D7',
    200: '#C8F2BA',
    300: '#C0F1B0',
    400: '#B8EFA6',
    500: '#B0ED9C',
    600: '#9ED58C',
    700: '#8DBE7D',
    800: '#7BA66D',
    900: '#58774E',
  },
  secondary: {
    50: '#FAF6FE',
    100: '#F3EAFD',
    200: '#E8D8FC',
    300: '#D7BAF8',
    400: '#C69CF4',
    500: '#A562EA',
    600: '#8F42DB',
    700: '#7A30C0',
    800: '#672C9D',
    900: '#55257E',
    950: '#380E5D',
  },
  red: {
    50: '#FFF0F0',
    100: '#FFDEDE',
    200: '#FFC3C3',
    300: '#FF9999',
    400: '#FF5E5E',
    500: '#FF2C2C',
    600: '#F61515',
    700: '#CF0606',
    800: '#AB0909',
    900: '#8D0F0F',
    950: '#4D0202',
  },
  orange: {
    50: '#FEF8EE',
    100: '#FCEFD8',
    200: '#F9DAAF',
    300: '#F5BF7C',
    400: '#F09A47',
    500: '#ED8936',
    600: '#DD6519',
    700: '#B74D17',
    800: '#923D1A',
    900: '#763418',
    950: '#3F180B',
  },
  green: {
    50: '#EFFDE8',
    100: '#DCF9CE',
    200: '#BAF4A2',
    300: '#8FEA6C',
    400: '#69DC3F',
    500: '#48C020',
    600: '#359B15',
    700: '#2A7615',
    800: '#255D17',
    900: '#234F18',
    950: '#0D2C07',
  },
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#C0DBFD',
    300: '#94C5FC',
    400: '#62A5F8',
    500: '#4285F4',
    600: '#2763E9',
    700: '#1F4ED6',
    800: '#2041AD',
    900: '#1F3B89',
    950: '#182553',
  },
} as const;

/** AI marketing gradient — retained from legacy theme until KDS variable is populated. */
export const aiGradient = 'linear-gradient(90.2deg, #04C3CC -0.81%, #527BE4 40.47%, #A373EB 100%)';

export const partialPrimaryGradient =
  'linear-gradient(90deg, rgba(9, 192, 206, 0.15) 0%, rgba(98, 122, 230, 0.15) 42.96%, rgba(161, 116, 235, 0.15) 100%)';

/** Soft orange fade (orange.500 @ 11%) — e.g. out-of-coverage jurisdiction cells. */
export const softOrangeFade =
  'linear-gradient(91.58deg, rgba(237, 137, 54, 0.11) 0.44%, rgba(255, 255, 255, 0) 72.19%)';
